<template>
	<div
		class="global-booker"
		:style="`--offset: ${navHeight}px; --scroll-offset: ${scrollOffset}px;`"
		:class="{ 'global-booker__nav': nav, 'global-booker__nav--sticky': isCarsPage, 'global-booker__nav--open': nav && state.booker }">
		<AccordionList v-if="nav" v-model:state="state">
			<AccordionItem id="booker" :defaultOpened="false">
				<template #summary>
					<div class="global-booker__container global-booker__container--mobile">
						<div class="global-booker__nav-all">
							<Transition name="fade" mode="out-in">
								<Skeletor v-if="!isHydrated" class="global-booker__button-skeletor" />
							</Transition>
							<div class="global-booker__nav-field">
								<div class="global-booker__nav-field-value">
									<span v-if="!startLocation">{{ $t('components.booker.pickup_location') }}</span>
									<span v-else>
										{{ title(startLocation) }}
									</span>
								</div>
								<div class="global-booker__nav-field-label">
									{{ format(dates.start, 'dd MMM, HH:mm', { locale: pl }) }} -
									{{ format(dates.end, 'dd MMM, HH:mm', { locale: pl }) }}
								</div>
							</div>
							<div class="global-booker__nav-field-icon">
								<GlobalIcon name="edit" :height="20"></GlobalIcon>
							</div>
						</div>
					</div>
				</template>
				<template #icon></template>
				<GlobalBookerContainer v-if="state.booker" nav accordion :location="location" />
			</AccordionItem>
		</AccordionList>

		<GlobalBookerContainer v-if="!state.booker" :nav="nav" :location="location" />
		<v-overlay v-model="overlay" attach persistent close-on-back scroll-strategy="none" @click="overlay = false" />
	</div>
</template>

<script lang="ts" setup>
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
// @ts-ignore "no types"
import { Skeletor } from 'vue-skeletor'
import { AccordionList, AccordionItem } from 'vue3-rich-accordion'
import { ELocationPurpose } from '~/data/bookerData'
import { useUrlFilters } from '@/modules/carsModule/composables/useUrlFilters'

const { y } = useWindowScroll()

const scrollOffset = computed(() => {
	return y.value > (navHeight ?? 0) ? y.value - 186 : 0
})
const { getStartDate, getEndDate } = useBookerService()
const navHeight = inject<number>('navHeight', 0)
const overlay = ref(true)
const bookingStore = useBookingStore()
const state = ref<Record<string, boolean>>({})
const route = useRoute()
const localePath = useLocalePath()
const isHydrated = ref(false)

const isCarsPage = computed(() => {
	return route.path === localePath('cars')
})

const { startLocation, endLocation, dates } = useBookerForm()

if (process.client) {
	const localDates = bookingStore.getLocalBookerDates
	const localLocations = bookingStore.getLocalBookerLocations

	const localStart = localDates?.start ? new Date(localDates.start) : null
	const localEnd = localDates?.end ? new Date(localDates.end) : null

	if (localStart && localEnd && bookingStore.isDatesCorrectForRenteon(localStart, localEnd)) {
		bookingStore.setBookerDates({ start: localStart, end: localEnd }, localDates?.source)
	} else {
		bookingStore.setBookerDates(
			{
				start: getStartDate(),
				end: getEndDate(),
			},
			EBookerDatesSource.AUTO,
		)
	}

	if (localLocations) {
		bookingStore.setBookerLocations(localLocations)
	}
}

onMounted(() => {
	isHydrated.value = true
})

watch(
	[() => startLocation.value, () => endLocation.value],
	() => {
		const start = startLocation.value
		if (start && !(start.crmID || start.isDelivery || start.purpose === ELocationPurpose.MEET_AND_GREET)) {
			startLocation.value = null
		}

		const end = endLocation.value
		if (end && !(end.crmID || end.isDelivery || start?.purpose === ELocationPurpose.MEET_AND_GREET)) {
			endLocation.value = null
		}
	},
	{
		deep: true,
		immediate: true,
	},
)

watch(
	() => state.value.booker,
	(isOpen: boolean) => {
		overlay.value = !!isOpen
	},
	{ immediate: true },
)

watch(
	() => overlay.value,
	() => {
		if (!overlay.value) state.value.booker = false
	},
)

watch(
	() => bookingStore.getBookerLocations?.end,
	(end: ILocationModel | null | undefined) => {
		if (end && !bookingStore.getIsOtherEndLocation) bookingStore.setOtherEndLocation(true)
	},
	{ immediate: true },
)

const urlFilters = useUrlFilters()
const locationsStore = useLocationsStore()

watch(
	() => (isCarsPage.value ? urlFilters.state.value : null),
	(s) => {
		if (!s) return
		if (s.dateOut) {
			const d = new Date(s.dateOut)
			if (!isNaN(d.getTime())) dates.value = { ...dates.value, start: d }
		}
		if (s.dateIn) {
			const d = new Date(s.dateIn)
			if (!isNaN(d.getTime())) dates.value = { ...dates.value, end: d }
		}
		if (s.officeId) {
			const locations = locationsStore.getLocations
			const found = locations?.find((l) => String(l.crmID) === s.officeId) ?? null
			if (found) startLocation.value = found
		}
	},
	{ deep: true, immediate: true },
)

watch(
	() => locationsStore.getLocations,
	(locations) => {
		if (!isCarsPage.value || !locations) return
		const officeId = urlFilters.state.value.officeId
		if (!officeId) return
		if (startLocation.value && String(startLocation.value.crmID) === officeId) return
		const found = locations.find((l) => String(l.crmID) === officeId)
		if (found) startLocation.value = found
	},
	{ immediate: true },
)

const { locale } = useI18n()
const i18nService = usei18nService()
// const locationsStore = useLocationsStore()

// onMounted(() => {
// 	locationsStore.fetchLocationCities()
// })

const title = (location: any) => {
	if (!location.city) return location.name
	else if (location.purpose === ELocationPurpose.MEET_AND_GREET) return `${i18nService.$tDynamic('data.cities', location.city)}, ${location.name}`
	else if ((location.city && location.icon === 'airplane') || (location.city && location.icon === 'train')) {
		return `${locale.value === 'pl' ? location.name : location.nameEn}`
	} else {
		return `${i18nService.$tDynamic('data.cities', location.city)}, ${location.addressStreet} ${location.addressHouseNumber ?? ''}`
	}
}

defineProps<{
	nav?: boolean
	location?: boolean
}>()

provide('state', state)
provide('isCarsPage', isCarsPage)
</script>

<style lang="scss">
@use '@/assets/scss/components/accordion';
@import 'vue-skeletor/dist/vue-skeletor.css';
</style>
<style lang="scss" src="./Booker.scss"></style>
